/** Per-campaign (membership-scoped) custom-field editor.
 *
 * These are the beneficiary values for a campaign's `applies_to="membership"`
 * fields — e.g. the LED campaign's installation_company / installation_date /
 * how_many_leds. Before this, there was no UI to *enter* them (only Excel
 * import / API); the beneficiary detail page could only display them read-only.
 *
 * Two exports share the same schema-driven inputs:
 *   - <MembershipFieldsFieldset> — controlled inputs for the add-beneficiary
 *     modal (parent owns the state, sends it as `membership_custom_fields`).
 *   - <MembershipFieldsCard> — a self-contained editable card (loads schema,
 *     local draft, Save → PATCH the membership) for the beneficiary detail.
 *
 * The schema is resolved with the campaign id only; the backend defaults the
 * country scope from the campaign, so country-scoped fields (country_scope=FR)
 * resolve without the caller having to thread the country.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { FieldDefinition } from "@/lib/types";

import { resolveFieldSchema } from "./api";

type Values = Record<string, string>;

function useMembershipSchema(campaign: string) {
  return useQuery({
    queryKey: ["fields-schema", "membership", campaign],
    queryFn: () => resolveFieldSchema({ applies_to: "membership", campaign }),
    enabled: !!campaign,
  });
}

/** One schema-driven input (text / textarea / number / date / dropdown). */
function FieldInput({
  fd,
  value,
  onChange,
  disabled,
}: {
  fd: FieldDefinition;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const common = "input w-full";
  if (fd.kind === "dropdown") {
    return (
      <select
        className={common}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">
          {t("leads.campaignFields.selectPlaceholder", "—")}
        </option>
        {fd.options_jsonb.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label || o.value}
          </option>
        ))}
      </select>
    );
  }
  if (fd.kind === "textarea") {
    return (
      <textarea
        className={`${common} min-h-[3.5rem]`}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      type={fd.kind === "number" ? "number" : fd.kind === "date" ? "date" : "text"}
      className={common}
      value={value}
      disabled={disabled}
      placeholder={fd.regex_pattern || undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function FieldRows({
  fields,
  values,
  onChange,
  disabled,
}: {
  fields: FieldDefinition[];
  values: Values;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {fields.map((fd) => (
        <label key={fd.id} className="space-y-1 text-xs text-nb-text-2">
          <span>
            {fd.label}
            {fd.is_required && " *"}
          </span>
          <FieldInput
            fd={fd}
            value={values[fd.key] ?? ""}
            onChange={(v) => onChange(fd.key, v)}
            disabled={disabled}
          />
          {fd.help_text && (
            <span className="block text-[10px] text-nb-text-3">{fd.help_text}</span>
          )}
        </label>
      ))}
    </div>
  );
}

/** Controlled fieldset for the add-beneficiary modal. Renders nothing when the
 * campaign declares no membership fields. */
export function MembershipFieldsFieldset({
  campaign,
  values,
  onChange,
}: {
  campaign: string;
  values: Values;
  onChange: (key: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const schema = useMembershipSchema(campaign);
  const fields = schema.data ?? [];
  if (fields.length === 0) return null;
  return (
    <section className="card space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("leads.campaignFields.heading", "Campaign fields")}
      </h2>
      <FieldRows fields={fields} values={values} onChange={onChange} />
    </section>
  );
}

/** Self-contained editable card for the beneficiary detail page — seeds a draft
 * from the stored values, saves via PATCH. `canEdit` gates the inputs+Save
 * (falls back to a read-only list). Renders nothing when there are no fields
 * AND nothing is stored. */
export function MembershipFieldsCard({
  campaign,
  membershipId,
  values,
  canEdit,
}: {
  campaign: string;
  membershipId: string;
  values: Record<string, unknown>;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const schema = useMembershipSchema(campaign);
  const fields = schema.data ?? [];

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Values>({});

  // Seed the draft from stored values whenever we open the editor.
  useEffect(() => {
    if (editing) {
      const seed: Values = {};
      for (const fd of fields) seed[fd.key] = String(values?.[fd.key] ?? "");
      setDraft(seed);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch(ApiPaths.campaignLead(campaign, membershipId), {
        custom_fields_jsonb: draft,
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contact-memberships"] });
      void qc.invalidateQueries({ queryKey: ["memberships"] });
      setEditing(false);
    },
  });

  const filled = fields.filter((fd) => (values?.[fd.key] ?? "") !== "");

  // Nothing to show and nothing to add → render nothing.
  if (fields.length === 0) return null;

  if (!editing) {
    return (
      <div className="mt-1.5 space-y-1">
        {filled.length > 0 && (
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
            {filled.map((fd) => (
              <FieldRowReadonly
                key={fd.id}
                label={fd.label}
                value={String(values[fd.key])}
              />
            ))}
          </dl>
        )}
        {canEdit && (
          <button
            type="button"
            className="text-xs text-nb-mint hover:underline"
            onClick={() => setEditing(true)}
          >
            {filled.length > 0
              ? t("leads.campaignFields.edit", "Edit campaign fields")
              : t("leads.campaignFields.add", "Add campaign fields")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-nb-border p-2">
      <FieldRows
        fields={fields}
        values={draft}
        onChange={(key, value) => setDraft((d) => ({ ...d, [key]: value }))}
        disabled={save.isPending}
      />
      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {t("leads.campaignFields.saveError", "Could not save. Check the values.")}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary text-xs"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? t("common.saving", "Saving…") : t("common.save", "Save")}
        </button>
        <button
          type="button"
          className="btn-ghost text-xs"
          disabled={save.isPending}
          onClick={() => setEditing(false)}
        >
          {t("common.cancel", "Cancel")}
        </button>
      </div>
    </div>
  );
}

function FieldRowReadonly({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-nb-text-3">{label}</dt>
      <dd className="text-nb-text-2">{value}</dd>
    </>
  );
}
